(() => {
  if (window.XMilesNutritionPlanPrint) return;

  const NITRATE_LEAD_IN_DAYS = 5;
  const NITRATE_PER_DOSE_MG = 400;
  const CARB_LOAD_SPORTS = new Set(["running", "triathlon", "cycling", "swim", "duathlon"]);

  const escapeHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

  const numberFrom = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const plannerNumber = (value) => {
    const rounded = Math.round(numberFrom(value) * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const durationLabel = (minutes) => {
    const total = Math.max(0, Math.round(numberFrom(minutes)));
    const hours = Math.floor(total / 60);
    const remainder = total % 60;
    if (!hours) return `${remainder} min`;
    if (!remainder) return `${hours} hr${hours === 1 ? "" : "s"}`;
    return `${hours} hr ${remainder} min`;
  };

  const runningCarbLoadGuidance = (plan = {}) => {
    const weight = Math.max(0, numberFrom(plan?.weight));
    const active = CARB_LOAD_SPORTS.has(String(plan?.sport || "").trim().toLowerCase()) && weight > 0;
    return {
      active,
      weight,
      minimum: active ? Math.round(weight * 8) : 0,
      maximum: active ? Math.round(weight * 12) : 0,
    };
  };

  const elapsedTime = (minutes) => {
    const total = Math.max(0, Math.round(numberFrom(minutes)));
    const hours = Math.floor(total / 60);
    const remainder = total % 60;
    return hours ? `${hours}:${String(remainder).padStart(2, "0")}` : `${remainder} min`;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(`${String(dateValue).slice(0, 10)}T12:00:00`);
    if (Number.isNaN(date.getTime())) return "";
    const day = date.getDate();
    const remainder = day % 100;
    const suffix = remainder >= 11 && remainder <= 13
      ? "th"
      : day % 10 === 1
        ? "st"
        : day % 10 === 2
          ? "nd"
          : day % 10 === 3 ? "rd" : "th";
    const monthAndYear = new Intl.DateTimeFormat("en-GB", {
      month: "long",
      year: "numeric",
    }).format(date);
    return `${day}${suffix} ${monthAndYear}`;
  };

  const phaseProducts = (selections, phase) =>
    Array.isArray(selections?.[phase]) ? selections[phase].filter(Boolean) : [];

  const beforeTimingForProduct = (product) => product?.beforeTiming === "day-before" ? "day-before" : "race-day";

  const planningSupplementKind = (product) => {
    if (product?.planningSupplementKind === "bicarb" || product?.planningSupplementKind === "nitrate") {
      return product.planningSupplementKind;
    }
    const copy = [
      product?.title,
      product?.variant,
      product?.productType,
      product?.product_type,
      product?.type,
      ...(Array.isArray(product?.tags) ? product.tags : []),
    ].filter(Boolean).join(" ").toLowerCase().replace(/[_-]+/g, " ");
    if (/\b(?:bicarb{1,2}|bicarbonate)\b/.test(copy)) return "bicarb";
    if (/\b(?:nitrate|beetroot|beet root)\b|\bbeet it\b/.test(copy)) return "nitrate";
    return "";
  };

  const uniqueProducts = (products) => {
    const seen = new Set();
    return products.filter((product) => {
      const key = String(product?.variantId || product?.itemId || `${product?.title || ""}|${product?.variant || ""}`).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const selectedSupplements = (selections, kind) => uniqueProducts(
    ["pre", "during", "post", "supplements"]
      .flatMap((phase) => phaseProducts(selections, phase))
      .filter((product) => planningSupplementKind(product) === kind)
  );

  const regularFuelProducts = (products) => products.filter((product) => !planningSupplementKind(product));

  const inferredServingsPerPurchase = (product) => {
    const explicit = Math.round(numberFrom(product?.servingsPerPurchase, 0));
    if (explicit > 0) return explicit;
    const copy = `${product?.variant || ""} ${product?.title || ""}`;
    const labelled = copy.match(/\b(?:box|pack|case|carton|pouch|tube)\s*(?:of|x)?\s*(\d{1,3})\b/i)
      || copy.match(/\b(\d{1,3})\s*(?:x|pack)\b/i);
    return labelled ? Math.max(1, Number(labelled[1]) || 1) : 1;
  };

  const servingCount = (product) => {
    const purchases = Math.max(1, Math.round(numberFrom(product?.quantity, 1)));
    if (product?.custom) return purchases;
    const servingsPerPurchase = inferredServingsPerPurchase(product);
    const availableServings = purchases * servingsPerPurchase;
    if (servingsPerPurchase <= 1) return availableServings;
    const selectedServings = Math.round(numberFrom(product?.servingsUsed, availableServings));
    return Math.max(1, Math.min(availableServings, selectedServings || availableServings));
  };

  const purchaseContainer = (product) => {
    const copy = `${product?.variant || ""} ${product?.title || ""}`.toLowerCase();
    const match = copy.match(/\b(pouch|tub|tube|box|pack|case|carton|bottle|jar)\b/);
    return match?.[1] || "pack";
  };

  const pluralise = (word, amount) => {
    if (amount === 1) return word;
    if (word === "box") return "boxes";
    return `${word}s`;
  };

  const servingUseSummary = (product, short = false) => {
    const purchases = Math.max(1, Math.round(numberFrom(product?.quantity, 1)));
    if (product?.custom) return `${purchases} custom serving${purchases === 1 ? "" : "s"}`;
    const servingsPerPurchase = inferredServingsPerPurchase(product);
    if (servingsPerPurchase <= 1) {
      return `${purchases} single serve${purchases === 1 ? "" : "s"}`;
    }
    const used = servingCount(product);
    const available = purchases * servingsPerPurchase;
    const container = purchaseContainer(product);
    const from = `${purchases} ${pluralise(container, purchases)}`;
    return short
      ? `${used} serving${used === 1 ? "" : "s"} from ${from}`
      : `Use ${used} serving${used === 1 ? "" : "s"} from ${from} (${available} available)`;
  };

  const scheduleServingDetail = (product) => {
    const variant = product?.variant || (product?.custom ? "Custom fuel" : "Selected serving");
    if (product?.custom || inferredServingsPerPurchase(product) <= 1) return variant;
    const planned = servingCount(product);
    const current = Math.max(1, Math.round(numberFrom(product?.servingIndex, 1)));
    return `${variant} · serving ${current} of ${planned} planned from the ${purchaseContainer(product)}`;
  };

  const servingNutrients = (product) => {
    const nutrients = product?.nutrients || {};
    const divisor = product?.nutrientsPerPurchase === true
      ? inferredServingsPerPurchase(product)
      : 1;
    return {
      calories: Math.max(0, numberFrom(nutrients.calories)) / divisor,
      carbs: Math.max(0, numberFrom(nutrients.carbs)) / divisor,
      protein: Math.max(0, numberFrom(nutrients.protein)) / divisor,
      sodium: Math.max(0, numberFrom(nutrients.sodium)) / divisor,
    };
  };

  const interleavedUnits = (products) => {
    const queues = products.map((product) => ({
      product,
      remaining: Math.min(48, servingCount(product)),
      total: servingCount(product),
      used: 0,
    }));
    const units = [];
    let pending = true;
    while (pending && units.length < 72) {
      pending = false;
      queues.forEach((queue) => {
        if (queue.remaining <= 0 || units.length >= 72) return;
        pending = true;
        queue.remaining -= 1;
        queue.used += 1;
        units.push({
          ...queue.product,
          nutrients: servingNutrients(queue.product),
          servingIndex: queue.used,
          servingTotal: queue.total,
        });
      });
    }
    units.omitted = Math.max(0, queues.reduce((sum, queue) => sum + queue.total, 0) - units.length);
    return units;
  };

  const productAction = (product) => {
    const copy = `${product?.title || ""} ${product?.variant || ""} ${product?.productType || product?.product_type || product?.type || ""}`.toLowerCase();
    if (/drink|mix|hydration|electrolyte|sachet|powder/.test(copy)) return "Sip";
    if (/bar|chew|block|blok|jelly/.test(copy)) return "Eat";
    if (/gel|shot/.test(copy)) return "Take";
    return "Use";
  };

  const isGelServing = (product) => {
    const copy = `${product?.title || ""} ${product?.variant || ""} ${product?.productType || product?.product_type || product?.type || ""}`.toLowerCase();
    return /\bgel(?:s)?\b/.test(copy) && productAction(product) === "Take";
  };

  const roundToFive = (minutes) => Math.max(0, Math.round(numberFrom(minutes) / 5) * 5);

  const buildBeforeSchedule = (products) => {
    const units = interleavedUnits(products);
    const count = Math.max(1, units.length);
    return units.map((product, index) => {
      const minutesBefore = count === 1
        ? 45
        : roundToFive(105 - (90 * index / Math.max(1, count - 1)));
      return {
        product,
        label: `${minutesBefore} min before`,
        minute: -minutesBefore,
        action: productAction(product),
      };
    });
  };

  const buildDayBeforeSchedule = (products) => products.map((product) => {
    const quantity = Math.max(1, Math.round(numberFrom(product?.quantity, 1)));
    return {
      product: {
        ...product,
        title: `${product?.title || "Selected fuel"}${quantity > 1 ? ` × ${quantity}` : ""}`,
      },
      label: "Across the day",
      minute: -1440,
      action: productAction(product),
    };
  });

  const servingCarbs = (product) => Math.max(0, numberFrom(product?.nutrients?.carbs));

  const buildEvenDuringSchedule = (units, duration) => {
    const interval = duration / units.length;
    const first = Math.min(30, Math.max(10, interval * 0.75));
    return units.map((product, index) => {
      const minute = Math.min(duration - 5, roundToFive(first + (interval * index)));
      const action = productAction(product);
      const nextMinute = Math.min(duration, roundToFive(first + (interval * (index + 1))));
      return {
        product,
        label: action === "Sip" && nextMinute > minute + 5
          ? `${elapsedTime(minute)}–${elapsedTime(nextMinute)}`
          : elapsedTime(minute),
        minute,
        action,
      };
    });
  };

  const buildDuringSchedule = (products, durationMinutes, sport, carbsPerHour) => {
    const units = interleavedUnits(products);
    const duration = Math.max(30, Math.round(numberFrom(durationMinutes, 60)));
    if (!units.length) return [];

    const planHourlyTarget = Math.max(0, numberFrom(carbsPerHour));
    if (!planHourlyTarget) return buildEvenDuringSchedule(units, duration);
    const totalSelectedCarbs = units.reduce((sum, product) => sum + servingCarbs(product), 0);
    // The plan target is a minimum. Use an effective schedule rate that places every
    // selected serving inside the event, even when the customer selects more fuel.
    const hourlyTarget = Math.max(planHourlyTarget, totalSelectedCarbs / Math.max(duration / 60, 0.5));
    const eventCarbBudget = Number.POSITIVE_INFINITY;

    const usesPreStartGel = duration > 60 && /\b(?:running|cycling)\b/i.test(String(sport || ""));
    const preStartGelIndex = usesPreStartGel
      ? units.findIndex((product) => {
          const carbs = servingCarbs(product);
          return isGelServing(product) && carbs > 0 && carbs <= hourlyTarget;
        })
      : -1;
    const schedule = [];
    const spareEntries = [];
    let coveredUntilMinute = 0;
    let scheduledCarbs = 0;

    if (preStartGelIndex >= 0) {
      const preStartGel = units.splice(preStartGelIndex, 1)[0];
      const preStartCarbs = servingCarbs(preStartGel);
      schedule.push({
        product: {
          ...preStartGel,
          variant: `${preStartGel?.variant || "Selected serving"} · ${plannerNumber(preStartCarbs)}g counts towards the first hour`,
        },
        label: "15 min before",
        minute: -15,
        action: productAction(preStartGel),
        preStart: true,
      });
      coveredUntilMinute = preStartCarbs / hourlyTarget * 60;
      scheduledCarbs = preStartCarbs;
    }

    units.forEach((product) => {
      const carbs = servingCarbs(product);
      const spansMoreThanOneHour = carbs > hourlyTarget;
      const standardAction = productAction(product);
      const useGradually = spansMoreThanOneHour || standardAction === "Sip";
      const remainingCarbs = Math.max(0, eventCarbBudget - scheduledCarbs);

      if (remainingCarbs <= 0.01) {
        spareEntries.push({
          product: {
            ...product,
            variant: `${product?.variant || "Selected serving"} · not required to meet this event's hourly target`,
          },
          label: "Carry as spare",
          minute: duration,
          action: "Pack",
          spare: true,
        });
        return;
      }

      if (carbs > remainingCarbs + 0.01 && useGradually) {
        if (useGradually && remainingCarbs >= 5 && coveredUntilMinute < duration) {
          const coverageMinutes = remainingCarbs / hourlyTarget * 60;
          const minute = roundToFive(coveredUntilMinute);
          const nextMinute = Math.min(duration, roundToFive(coveredUntilMinute + coverageMinutes));
          schedule.push({
            product: {
              ...product,
              variant: `${product?.variant || "Selected serving"} · use approx. ${plannerNumber(remainingCarbs)}g of this ${plannerNumber(carbs)}g serving; keep the remainder as spare`,
            },
            label: `${elapsedTime(minute)}–${elapsedTime(nextMinute)}`,
            minute,
            action: "Use gradually",
            coverageMinutes,
            partial: true,
          });
          scheduledCarbs += remainingCarbs;
          coveredUntilMinute = duration;
        } else {
          spareEntries.push({
            product: {
              ...product,
              variant: `${product?.variant || "Selected serving"} · not required to meet this event's hourly target`,
            },
            label: "Carry as spare",
            minute: duration,
            action: "Pack",
            spare: true,
          });
        }
        return;
      }

      const coverageMinutes = carbs > 0 ? carbs / hourlyTarget * 60 : 30;
      const windowEndMinute = coveredUntilMinute + coverageMinutes;
      // A discrete serving starts fuelling the next window, so show it at the
      // beginning of that window rather than after its carbohydrate has been used.
      const minute = Math.min(Math.max(0, duration - 5), roundToFive(coveredUntilMinute));
      const nextMinute = roundToFive(windowEndMinute);
      const action = spansMoreThanOneHour ? "Use gradually" : standardAction;
      schedule.push({
        product,
        label: useGradually && nextMinute > minute + 5
          ? `${elapsedTime(minute)}–${elapsedTime(nextMinute)}`
          : elapsedTime(minute),
        minute,
        action,
        coverageMinutes,
      });
      coveredUntilMinute = Math.min(duration, windowEndMinute);
      scheduledCarbs += carbs;
    });

    return [...schedule, ...spareEntries];
  };

  const buildAfterSchedule = (products) => {
    const units = interleavedUnits(products);
    return units.map((product, index) => {
      const minute = Math.min(60, 15 + (index * 15));
      return {
        product,
        label: index === 0 ? "Within 15 min" : `Within ${minute} min`,
        minute,
        action: productAction(product),
      };
    });
  };

  const productName = (product) => {
    const suffix = product?.servingTotal > 1
      ? ` (${product.servingIndex} of ${product.servingTotal})`
      : "";
    return `${product?.title || "Selected fuel"}${suffix}`;
  };

  const scheduleRowsMarkup = (schedule, emptyCopy) => {
    if (!schedule.length) return `<p class="empty">${escapeHtml(emptyCopy)}</p>`;
    return schedule.map((entry, index) => `
      <article class="schedule-row">
        <span class="schedule-number">${index + 1}</span>
        <time>${escapeHtml(entry.label)}</time>
        <div>
          <strong>${escapeHtml(entry.action)} ${escapeHtml(productName(entry.product))}</strong>
          <small>${escapeHtml(scheduleServingDetail(entry.product))}</small>
        </div>
        <span class="check" aria-label="Checklist box"></span>
      </article>
    `).join("");
  };

  const buildBicarbSchedule = (products) => uniqueProducts(products).map((product) => ({
    product: {
      ...product,
      variant: `${product?.variant || "Selected serving"} · water only for 20 min either side`,
    },
    label: "2 hr before",
    minute: -120,
    action: "Take",
    bicarb: true,
  }));

  const buildNitrateSchedules = (products) => {
    const product = uniqueProducts(products)[0];
    if (!product) return { dayBefore: [], raceDay: [] };
    return {
      dayBefore: [{
        product: {
          ...product,
          variant: `One ${NITRATE_PER_DOSE_MG}mg serving daily from Day -${NITRATE_LEAD_IN_DAYS} to Day -1`,
        },
        label: `Day -${NITRATE_LEAD_IN_DAYS} to -1`,
        minute: -1440,
        action: "Take",
        nitrate: true,
      }],
      raceDay: [{
        product: {
          ...product,
          variant: `Final ${NITRATE_PER_DOSE_MG}mg serving · dose 6 of 6`,
        },
        label: "Race morning",
        minute: -180,
        action: "Take",
        nitrate: true,
      }],
    };
  };

  const supplementTimelineMarkup = (bicarbProducts, nitrateProducts) => {
    if (!bicarbProducts.length && !nitrateProducts.length) return "";
    return `
      <div class="supplement-callouts" aria-label="Performance supplement timing">
        ${nitrateProducts.length ? `<div class="supplement-callout is-nitrate"><span>Nitrate plan</span><strong>Day -${NITRATE_LEAD_IN_DAYS} to race morning</strong><small>${NITRATE_PER_DOSE_MG}mg nitrate per dose</small></div>` : ""}
        ${bicarbProducts.length ? `<div class="supplement-callout is-bicarb"><span>Bicarb</span><strong>2 hours before start</strong><small>Water only for 20 min either side</small></div>` : ""}
      </div>
    `;
  };

  const nitrateLoadMarkup = (products) => {
    if (!products.length) return "";
    const productNames = uniqueProducts(products).map((product) => product.title || "Selected nitrate product").join(" · ");
    return `
      <section class="nitrate-load">
        <header><div><span>Nitrate · six-dose plan</span><strong>Five daily doses before the event + one on race morning</strong></div><small>${escapeHtml(productNames)}</small></header>
        <div class="nitrate-load-days">
          ${[5, 4, 3, 2, 1].map((day) => `<div><span>Day -${day}</span><strong>${NITRATE_PER_DOSE_MG}mg</strong><i></i></div>`).join("")}
          <div class="is-race-morning"><span>Race morning</span><strong>${NITRATE_PER_DOSE_MG}mg</strong><i></i></div>
        </div>
        <p>Use one ${NITRATE_PER_DOSE_MG}mg serving on each of the five days before the event, then one final serving on race morning. Rehearse the same product and routine before a lower-priority event.</p>
      </section>
    `;
  };

  const duringTimelineMarkup = (schedule, durationMinutes) => {
    const duration = Math.max(30, Math.round(numberFrom(durationMinutes, 60)));
    if (!schedule.length) {
      return '<div class="timeline-empty"><strong>No during-session products selected</strong><span>Add fuel to the During phase of the live plan, then create this printout again.</span></div>';
    }
    const numberedSchedule = schedule
      .map((entry, index) => ({ ...entry, scheduleNumber: index + 1 }))
    const preStartEntry = numberedSchedule.find((entry) => entry.preStart || numberFrom(entry.minute) < 0);
    const timelineSchedule = numberedSchedule.filter((entry) => !entry.spare && numberFrom(entry.minute) >= 0);
    const preStartMarker = preStartEntry
      ? `<span class="timeline-pre-line" aria-hidden="true"></span><span class="timeline-marker is-pre-start"><b>${preStartEntry.scheduleNumber}</b><i>${escapeHtml(preStartEntry.label)}</i></span>`
      : "";
    const markers = timelineSchedule.slice(0, 24).map((entry, index) => {
      const position = Math.max(1.5, Math.min(98.5, entry.minute / duration * 100));
      return `<span class="timeline-marker${index % 2 ? " is-offset" : ""}" style="left:${position}%"><b>${entry.scheduleNumber}</b><i>${escapeHtml(elapsedTime(entry.minute))}</i></span>`;
    }).join("");
    const hiddenCount = Math.max(0, timelineSchedule.length - 24);
    return `
      <div class="timeline-track" aria-label="Fuel timing across the session">
        <span class="timeline-start"><b>START</b><small>0:00</small></span>
        <span class="timeline-line"></span>
        ${preStartMarker}
        ${markers}
        <span class="timeline-finish"><b>FINISH</b><small>${escapeHtml(elapsedTime(duration))}</small></span>
      </div>
      ${hiddenCount ? `<p class="timeline-note">The first 24 timings are marked above; all ${schedule.length} timings are listed below.</p>` : ""}
    `;
  };

  const selectedTotal = (products, nutrient) => products.reduce((sum, product) =>
    sum + Math.max(0, numberFrom(servingNutrients(product)?.[nutrient])) * servingCount(product), 0
  );

  const phaseTargetMarkup = (plan, selections) => {
    const beforeProducts = [
      ...regularFuelProducts(phaseProducts(selections, "pre")),
      ...selectedSupplements(selections, "bicarb"),
    ];
    const duringProducts = regularFuelProducts(phaseProducts(selections, "during"));
    const afterProducts = regularFuelProducts(phaseProducts(selections, "post"));
    const carbLoad = runningCarbLoadGuidance(plan);
    const selectedBeforeCarbs = selectedTotal(beforeProducts, "carbs");
    const selectedDuringCarbs = selectedTotal(duringProducts, "carbs");
    const selectedAfterCarbs = selectedTotal(afterProducts, "carbs");
    const selectedAfterProtein = selectedTotal(afterProducts, "protein");
    const plannedDuringCarbs = Math.max(0, numberFrom(plan?.during?.carbs))
      || Math.max(0, numberFrom(plan?.during?.carbsPerHour)) * Math.max(0, numberFrom(plan?.durationMinutes)) / 60;
    return `
      <div class="target${carbLoad.active ? " is-split" : ""}"><span>Before</span>${carbLoad.active ? `<div><p><small>Race day</small><strong>${plannerNumber(selectedBeforeCarbs)}g</strong><em>${plannerNumber(plan?.pre?.carbs)}g planned minimum</em></p><p><small>Day-before guidance</small><strong>${plannerNumber(carbLoad.minimum)}-${plannerNumber(carbLoad.maximum)}g</strong><em>Across familiar food and drinks</em></p></div>` : `<strong>${plannerNumber(selectedBeforeCarbs)}g carbs</strong><small>${plannerNumber(plan?.pre?.carbs)}g planned minimum</small>`}</div>
      <div class="target is-primary"><span>During</span><strong>${plannerNumber(plan?.during?.carbsPerHour)}g per hour</strong><small>${plannerNumber(selectedDuringCarbs)}g selected · ${plannerNumber(plannedDuringCarbs)}g planned minimum</small></div>
      <div class="target is-split"><span>After</span><div><p><small>Carbohydrate</small><strong>${plannerNumber(selectedAfterCarbs)}g</strong><em>${plannerNumber(plan?.post?.carbs)}g planned</em></p><p><small>Protein</small><strong>${plannerNumber(selectedAfterProtein)}g</strong><em>${plannerNumber(plan?.post?.protein)}g planned</em></p></div></div>
    `;
  };

  const racePhaseTimelineMarkup = (plan) => {
    const carbLoad = runningCarbLoadGuidance(plan);
    if (!carbLoad.active) return "";
    return `
      <div class="race-phase-timeline" aria-label="Race-day nutrition phases through recovery">
        <div><span>Before · Race Day</span><strong>${plannerNumber(plan?.pre?.carbs)}g</strong><small>before the start</small></div>
        <i>›</i>
        <div><span>During</span><strong>${plannerNumber(plan?.during?.carbsPerHour)}g/hr</strong><small>carbohydrate</small></div>
        <i>›</i>
        <div><span>After</span><strong>${plannerNumber(plan?.post?.protein)}g</strong><small>protein target</small></div>
      </div>
    `;
  };

  const checklistProductLink = (product) => {
    try {
      const parsed = new URL(product?.productUrl || "", "https://xmiles.co.uk");
      if (!/^\/products\//i.test(parsed.pathname)) return "";
      const variantId = String(product?.variantId || "").replace(/\D/g, "");
      if (variantId) parsed.searchParams.set("variant", variantId);
      return `https://xmiles.co.uk${parsed.pathname}${parsed.search}`;
    } catch (error) {
      return "";
    }
  };

  const checklistProductQr = (product) => {
    const productUrl = checklistProductLink(product);
    if (!productUrl) return '<span class="product-qr-spacer"></span>';
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&format=svg&margin=0&data=${encodeURIComponent(productUrl)}`;
    const productName = product?.title || "this product";
    return `<a class="product-qr" href="${escapeHtml(productUrl)}" title="Open ${escapeHtml(productName)}"><img src="${escapeHtml(qrUrl)}" alt="QR code linking to ${escapeHtml(productName)}"><span>Scan</span></a>`;
  };

  const productChecklistMarkup = (selections, plan) => {
    const beforeProducts = phaseProducts(selections, "pre");
    const nitrateProducts = selectedSupplements(selections, "nitrate");
    const optionalSupplements = phaseProducts(selections, "supplements")
      .filter((product) => planningSupplementKind(product) !== "nitrate");
    const phases = [
      ["Before / Race Day", uniqueProducts([...beforeProducts, ...nitrateProducts])],
      ["During", phaseProducts(selections, "during")],
      ["After", phaseProducts(selections, "post")],
      ["Optional supplements", optionalSupplements],
    ];
    return phases.map(([label, products]) => {
      if (!products.length) return "";
      return `
        <section class="checklist-phase">
          <h3>${escapeHtml(label)}</h3>
          ${products.map((product) => `
            <article>
              <span class="check"></span>
              ${product?.image ? `<img src="${escapeHtml(product.image)}" alt="">` : '<span class="food-icon">+</span>'}
              <div><strong>${escapeHtml(product.title || "Selected fuel")}</strong><small>${escapeHtml(product.variant || (product.custom ? "Custom fuel" : "Selected serving"))}</small></div>
              <b>${escapeHtml(servingUseSummary(product, true))}${inferredServingsPerPurchase(product) > 1 ? `<small>${escapeHtml(servingUseSummary(product))}</small>` : ""}</b>
              ${checklistProductQr(product)}
            </article>
          `).join("")}
        </section>
      `;
    }).filter(Boolean).join("");
  };

  const compactScheduleInstructions = (schedule, maximumMinute, maximumItems = 4) => {
    const eligible = schedule.filter((entry) =>
      numberFrom(entry.minute) < 0 || numberFrom(entry.minute) <= maximumMinute
    );
    if (!eligible.length) return "Add products to this phase before printing the rehearsal plan.";
    const shown = eligible.slice(0, maximumItems);
    const instructions = shown.map((entry) =>
      `${entry.label} - ${entry.action} ${productName(entry.product)}`
    ).join("; ");
    const remaining = Math.max(0, eligible.length - shown.length);
    return `${instructions}${remaining ? `; then continue with ${remaining} more planned timing${remaining === 1 ? "" : "s"}` : ""}.`;
  };

  const uniquePracticeProducts = (products) => {
    const seen = new Set();
    return products.filter((product) => {
      const key = String(product?.variantId || `${product?.title || ""}|${product?.variant || ""}`).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const practiceProductGroupsMarkup = (beforeSchedule, duringSchedule, afterSchedule) => {
    const groups = [
      ["Before", uniquePracticeProducts(beforeSchedule.map((entry) => entry.product))],
      ["During", uniquePracticeProducts(duringSchedule.map((entry) => entry.product))],
      ["After", uniquePracticeProducts(afterSchedule.map((entry) => entry.product))],
    ];
    return groups.map(([label, products]) => `
      <section class="practice-product-group">
        <span>${escapeHtml(label)}</span>
        ${products.length
          ? products.map((product) => `<div><strong>${escapeHtml(product.title || "Selected fuel")}</strong><small>${escapeHtml(product.variant || (product.custom ? "Custom fuel" : "Selected serving"))}</small></div>`).join("")
          : '<div><strong>No product selected</strong><small>Add one in the live plan before printing again.</small></div>'}
      </section>
    `).join("");
  };

  const practiceTimelineEntries = (beforeSchedule, duringSchedule, afterSchedule, maximumMinute, maximumDuringItems) => {
    const entries = [];
    if (beforeSchedule.length) {
      const before = beforeSchedule[beforeSchedule.length - 1];
      entries.push({ ...before, phase: "Before" });
    }
    const during = duringSchedule
      .filter((entry) => !entry.spare && (numberFrom(entry.minute) < 0 || numberFrom(entry.minute) <= maximumMinute))
      .slice(0, maximumDuringItems)
      .map((entry) => ({ ...entry, phase: entry.preStart || numberFrom(entry.minute) < 0 ? "Before start" : "During" }));
    entries.push(...during);
    if (afterSchedule.length) entries.push({ ...afterSchedule[0], phase: "After" });
    return entries;
  };

  const practiceTimelineMarkup = (label, focus, entries, hiddenCount = 0) => `
    <article class="practice-timeline">
      <header><strong>${escapeHtml(label)}</strong><small>${escapeHtml(focus)}</small></header>
      <div class="practice-timeline-track" role="list">
        ${entries.length ? entries.map((entry) => `
          <div class="practice-step" role="listitem">
            <span>${escapeHtml(entry.label)}</span>
            <strong>${escapeHtml(entry.product?.title || "Selected fuel")}</strong>
            <small>${escapeHtml(entry.action)}</small>
          </div>
        `).join("") : '<p>No products selected for this rehearsal yet.</p>'}
        ${hiddenCount > 0 ? `<div class="practice-step is-more" role="listitem"><span>Continue plan</span><strong>${hiddenCount} more timing${hiddenCount === 1 ? "" : "s"}</strong><small>Follow the event timeline</small></div>` : ""}
      </div>
    </article>
  `;

  const trainingSupplementMarkup = (bicarbProducts, nitrateProducts) => {
    if (!bicarbProducts.length && !nitrateProducts.length) return "";
    return `
      <div class="training-supplements">
        ${bicarbProducts.length ? `<article><span>Bicarb rehearsal</span><strong>Test the full routine before a hard session</strong><p>Use ${escapeHtml(bicarbProducts.map((product) => product.title || "your selected bicarb").join(" / "))} two hours before. Avoid other nutrition for 20 minutes either side (water is fine), then record stomach comfort and how the effort felt.</p></article>` : ""}
        ${nitrateProducts.length ? `<article><span>Nitrate rehearsal</span><strong>Practise the full six-dose routine before a lower-priority event</strong><p>Use one ${NITRATE_PER_DOSE_MG}mg serving daily from Day -${NITRATE_LEAD_IN_DAYS} to Day -1, then one on race morning. Keep the same selected product and note tolerance before using the routine for your main event.</p></article>` : ""}
      </div>
    `;
  };

  const runningTrainingPlanMarkup = (plan, beforeSchedule, duringSchedule, afterSchedule, bicarbProducts = [], nitrateProducts = []) => {
    const runningTips = [
      "Try every selected product before race day so the flavour, texture and serving size are familiar.",
      "Rehearse the complete timing plan in a race-like session using the same pace, bottles and carrying setup.",
      "Only use the final plan on race day once it feels comfortable to consume and digest in training.",
    ];
    return `
      <div class="running-training-plan">
        <p class="training-intro">Use these products before race day, then practise the full plan in a session that feels like the event. Keep the same timings, bottles and carrying setup so there are no surprises on the day.</p>
        ${trainingSupplementMarkup(bicarbProducts, nitrateProducts)}
        <div class="practice-products">${practiceProductGroupsMarkup(beforeSchedule, duringSchedule, afterSchedule)}</div>
        <ul class="training-checklist">${runningTips.map((tip) => `<li>${escapeHtml(tip)}</li>`).join("")}</ul>
      </div>
    `;
  };

  const eventTips = (plan) => {
    const copy = `${plan?.sport || ""} ${plan?.distance || ""}`.toLowerCase();
    const tips = [
      "Practise this exact product order and timing in at least two or three key training sessions.",
      "Set alerts on your watch or bike computer so you fuel before hunger or fatigue appears.",
      "Carry one spare serving on event day in case the session runs longer than predicted.",
    ];
    if (/marathon|50k|50m|100k|100m|long/.test(copy)) {
      tips.push("Begin fuelling early and keep the gaps consistent; do not try to catch up with several servings at once.");
    } else if (/cycling|triathlon|duathlon/.test(copy)) {
      tips.push("Arrange fuel in order of use and rehearse opening it safely while moving.");
    }
    return tips.slice(0, 4);
  };

  const printableMarkup = ({ plan, selections, planUrl, logoUrl }) => {
    const carbLoad = runningCarbLoadGuidance(plan);
    const eventDateLabel = formatDate(plan?.date);
    const beforeProducts = phaseProducts(selections, "pre");
    const bicarbProducts = selectedSupplements(selections, "bicarb");
    const nitrateProducts = selectedSupplements(selections, "nitrate");
    const nitrateSchedules = buildNitrateSchedules(nitrateProducts);
    const normalBeforeSchedule = buildBeforeSchedule(regularFuelProducts(beforeProducts));
    const beforeSchedule = [...nitrateSchedules.raceDay, ...buildBicarbSchedule(bicarbProducts), ...normalBeforeSchedule]
      .sort((first, second) => numberFrom(first.minute) - numberFrom(second.minute));
    const duringSchedule = buildDuringSchedule(
      regularFuelProducts(phaseProducts(selections, "during")),
      plan?.durationMinutes,
      plan?.sport,
      plan?.during?.carbsPerHour
    );
    const afterSchedule = buildAfterSchedule(regularFuelProducts(phaseProducts(selections, "post")));
    const eventLine = [plan?.sport, plan?.distance].filter(Boolean).join(" · ") || "Nutrition plan";
    const safePlanUrl = planUrl || window.location.href;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&format=svg&margin=0&data=${encodeURIComponent(safePlanUrl)}`;
    const trainingTips = eventTips(plan);
    const duringTarget = Math.max(0, numberFrom(plan?.during?.carbsPerHour));
    const selectedDuringCarbs = selectedTotal(regularFuelProducts(phaseProducts(selections, "during")), "carbs");
    const timelineGuidance = duringTarget > 0
      ? `The ${plannerNumber(duringTarget)}g/hour target is a minimum. All ${plannerNumber(selectedDuringCarbs)}g selected for During are scheduled within the event, including any 15-minute pre-start serving.`
      : "Timings use elapsed event time. Start early, fuel consistently and use a watch alert so no serving is missed.";
    const runningTrainingPlan = runningTrainingPlanMarkup(plan, beforeSchedule, duringSchedule, afterSchedule, bicarbProducts, nitrateProducts);
    const beforeLabel = carbLoad.active ? "Before / Race Day" : "Before";
    const brand = logoUrl
      ? `<img class="brand-logo" src="${escapeHtml(logoUrl)}" alt="XMiles">`
      : '<div class="brand-wordmark"><i>X</i><strong>MILES</strong></div>';

    return `<!doctype html>
      <html lang="en-GB">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width,initial-scale=1">
          <title>${escapeHtml(plan?.title || "Nutrition plan")} – XMiles</title>
          <style>
            @font-face{font-family:Poppins;font-style:normal;font-weight:400;font-display:swap;src:url("https://xmiles.co.uk/cdn/fonts/poppins/poppins_n4.0ba78fa5af9b0e1a374041b3ceaadf0a43b41362.woff2") format("woff2"),url("https://xmiles.co.uk/cdn/fonts/poppins/poppins_n4.214741a72ff2596839fc9760ee7a770386cf16ca.woff") format("woff")}
            @font-face{font-family:Poppins;font-style:normal;font-weight:700;font-display:swap;src:url("https://xmiles.co.uk/cdn/fonts/poppins/poppins_n7.56758dcf284489feb014a026f3727f2f20a54626.woff2") format("woff2"),url("https://xmiles.co.uk/cdn/fonts/poppins/poppins_n7.f34f55d9b3d3205d2cd6f64955ff4b36f0cfd8da.woff") format("woff")}
            :root{--ink:#001f31;--blue:#059de4;--pale:#eef8fc;--line:#d8e4e9;--muted:#58707c;--green:#2c9651;--paper:#fff}
            *{box-sizing:border-box}body,button,input,select,textarea{font-family:Poppins,Arial,Helvetica,sans-serif}body{margin:0;background:#edf2f4;color:var(--ink);-webkit-print-color-adjust:exact;print-color-adjust:exact}
            .toolbar{position:sticky;z-index:20;top:0;display:flex;padding:12px 20px;align-items:center;justify-content:space-between;gap:16px;background:#001f31;color:#fff;box-shadow:0 5px 18px rgba(0,31,49,.18)}
            .toolbar div{display:flex;align-items:center;gap:10px}.toolbar strong{font-size:14px}.toolbar span{color:#bcd0d9;font-size:12px}.toolbar button{min-height:38px;padding:8px 15px;border:1px solid #476371;border-radius:5px;background:transparent;color:#fff;font:700 12px Poppins,Arial,sans-serif;cursor:pointer}.toolbar button.primary{border-color:var(--blue);background:var(--blue)}
            .page{width:min(210mm,calc(100% - 32px));max-width:210mm;min-height:297mm;margin:22px auto;background:var(--paper);box-shadow:0 16px 45px rgba(0,31,49,.12)}
            .sheet{width:100%;min-height:297mm;padding:10mm}.sheet+ .sheet{border-top:8px solid #edf2f4}
            .document-header{display:grid;padding-bottom:22px;grid-template-columns:1fr auto;gap:24px;border-bottom:2px solid var(--ink)}
            .brand-logo{display:block;width:auto;max-width:170px;height:42px;object-fit:contain;object-position:left center}.brand-wordmark{display:flex;align-items:center;gap:5px;font-size:26px}.brand-wordmark i{display:grid;width:36px;height:36px;place-items:center;border:3px solid var(--blue);border-radius:50%;color:var(--blue);font-style:normal}
            .document-type{display:flex;align-items:flex-start;flex-direction:column;gap:5px;text-align:right}.document-type span{color:var(--blue);font-size:10px;font-weight:800;letter-spacing:.13em;text-transform:uppercase}.document-type strong{font-size:18px}
            .hero{display:grid;padding:26px 0 20px;grid-template-columns:minmax(0,1fr) auto;gap:30px;align-items:end}.hero .eyebrow{margin:0 0 7px;color:var(--blue);font-size:10px;font-weight:800;letter-spacing:.11em;text-transform:uppercase}.hero h1{max-width:720px;margin:0;font-size:38px;line-height:.98;letter-spacing:-.045em}.hero p{margin:11px 0 0;color:var(--muted);font-size:13px}.event-meta{display:grid;min-width:260px;grid-template-columns:1fr 1fr;border:1px solid var(--line);border-radius:8px;overflow:hidden}.event-meta.is-single{min-width:135px;grid-template-columns:1fr}.event-meta div{padding:11px 13px}.event-meta div+div{border-left:1px solid var(--line)}.event-meta span{display:block;margin-bottom:4px;color:var(--muted);font-size:8px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}.event-meta strong{font-size:12px}
            .targets{display:grid;margin-bottom:20px;grid-template-columns:repeat(3,1fr);border:1px solid var(--line);border-radius:8px;overflow:hidden}.targets.has-day-before{grid-template-columns:repeat(3,1fr)}.target{padding:13px 15px;background:#fafcfd}.target+.target{border-left:1px solid var(--line)}.target.is-primary{background:var(--pale)}.target span,.target strong,.target small{display:block}.target span{margin-bottom:4px;color:var(--blue);font-size:9px;font-weight:800;text-transform:uppercase}.target strong{font-size:18px}.target small{margin-top:3px;color:var(--muted);font-size:9px}.target.is-split>div{display:grid;margin-top:5px;grid-template-columns:1fr 1fr;gap:10px}.target.is-split p{margin:0}.target.is-split p+p{padding-left:10px;border-left:1px solid var(--line)}.target.is-split p small{margin:0;color:var(--blue);font-size:7px;font-weight:800;text-transform:uppercase}.target.is-split p strong{margin-top:2px;font-size:14px}.target.is-split p em{display:block;margin-top:2px;color:var(--muted);font-size:7px;font-style:normal}
            .training-rule{display:grid;margin:0 0 22px;padding:15px 18px;grid-template-columns:auto 1fr;gap:13px;border-radius:8px;background:var(--ink);color:#fff}.training-rule b{display:grid;width:30px;height:30px;place-items:center;border-radius:50%;background:var(--blue);font-size:17px}.training-rule strong{display:block;margin-bottom:4px;font-size:13px}.training-rule span{display:block;color:#d3e2e8;font-size:10px;line-height:1.5}
            .section-heading{display:flex;margin:0 0 12px;align-items:end;justify-content:space-between;gap:20px}.section-heading span{display:block;margin-bottom:4px;color:var(--blue);font-size:9px;font-weight:800;letter-spacing:.1em;text-transform:uppercase}.section-heading h2{margin:0;font-size:23px;letter-spacing:-.025em}.section-heading p{max-width:410px;margin:0;color:var(--muted);font-size:10px;line-height:1.45;text-align:right}
            .timeline{margin-bottom:18px;padding:18px 20px 14px;border:1px solid var(--line);border-radius:8px;background:#fbfdfe}.timeline-track{position:relative;height:90px;margin:4px 46px 0}.timeline-line{position:absolute;top:38px;right:0;left:0;height:4px;border-radius:5px;background:#d6e2e7}.timeline-line:after{position:absolute;top:0;right:0;bottom:0;left:0;width:100%;background:linear-gradient(90deg,var(--blue),#47b8e9);content:""}.timeline-pre-line{position:absolute;top:39px;right:100%;width:6.5%;border-top:2px dashed #8fa8b4}.timeline-start,.timeline-finish{position:absolute;z-index:2;top:26px;display:flex;align-items:center;flex-direction:column;gap:14px}.timeline-start{right:100%;transform:translateX(50%)}.timeline-finish{left:100%;transform:translateX(-50%)}.timeline-start:before,.timeline-finish:before{width:26px;height:26px;border:5px solid #fff;border-radius:50%;background:var(--ink);box-shadow:0 0 0 1px var(--ink);content:""}.timeline-start b,.timeline-finish b{position:absolute;top:-18px;font-size:7px}.timeline-start small,.timeline-finish small{font-size:8px;font-weight:700;white-space:nowrap}.timeline-marker{position:absolute;z-index:3;top:27px;display:flex;align-items:center;flex-direction:column;transform:translateX(-50%)}.timeline-marker b{display:grid;width:25px;height:25px;place-items:center;border:4px solid #fff;border-radius:50%;background:var(--blue);color:#fff;font-size:8px;box-shadow:0 0 0 1px var(--blue)}.timeline-marker i{margin-top:8px;color:var(--muted);font-size:7px;font-style:normal;font-weight:700;white-space:nowrap}.timeline-marker.is-offset i{transform:translateY(12px)}.timeline-marker.is-pre-start{left:-6.5%}.timeline-marker.is-pre-start b{background:var(--ink);box-shadow:0 0 0 1px var(--ink)}.timeline-marker.is-pre-start i{color:var(--ink);font-size:6.5px}.timeline-note{margin:0;color:var(--muted);font-size:8px;text-align:center}.timeline-empty{display:flex;min-height:82px;align-items:center;justify-content:center;flex-direction:column;gap:5px;color:var(--muted)}.timeline-empty strong{color:var(--ink)}
            .race-phase-timeline{display:grid;margin-bottom:10px;padding-bottom:10px;grid-template-columns:minmax(0,1fr) 22px minmax(0,1fr) 22px minmax(0,1fr);align-items:center;border-bottom:1px solid var(--line)}.race-phase-timeline>div{min-width:0}.race-phase-timeline span,.race-phase-timeline strong,.race-phase-timeline small{display:block}.race-phase-timeline span{color:var(--blue);font-size:7px;font-weight:800;text-transform:uppercase}.race-phase-timeline strong{margin-top:2px;font-size:11px}.race-phase-timeline small{margin-top:1px;color:var(--muted);font-size:6.5px}.race-phase-timeline i{color:#9bb0ba;font-size:16px;font-style:normal;text-align:center}
            .supplement-callouts{display:flex;margin:0 0 8px;gap:7px}.supplement-callout{display:grid;min-width:0;padding:6px 9px;grid-template-columns:auto auto auto;gap:7px;align-items:center;border:1px solid #bfe2f2;border-radius:6px;background:#eef9fe}.supplement-callout span{color:var(--blue);font-size:7px;font-weight:800;text-transform:uppercase}.supplement-callout strong{font-size:8px}.supplement-callout small{color:var(--muted);font-size:7px}.supplement-callout.is-bicarb{border-color:#b6d8e6;background:#f0f8fb}.nitrate-load{margin:0 0 9px;padding:8px 10px;border:1px solid #bfe2f2;border-radius:7px;background:#f4fbfe}.nitrate-load header{display:flex;margin-bottom:6px;align-items:end;justify-content:space-between;gap:12px}.nitrate-load header span,.nitrate-load header strong{display:block}.nitrate-load header span{color:var(--blue);font-size:7px;font-weight:800;text-transform:uppercase}.nitrate-load header strong{margin-top:1px;font-size:10px}.nitrate-load header small{overflow:hidden;max-width:45%;color:var(--muted);font-size:6.5px;text-overflow:ellipsis;white-space:nowrap}.nitrate-load-days{display:grid;grid-template-columns:repeat(6,1fr);border:1px solid #d7e8ef;border-radius:5px;overflow:hidden;background:#fff}.nitrate-load-days div{position:relative;padding:5px 7px}.nitrate-load-days div+div{border-left:1px solid #d7e8ef}.nitrate-load-days span,.nitrate-load-days strong{display:block}.nitrate-load-days span{color:var(--muted);font-size:6px;font-weight:800;text-transform:uppercase}.nitrate-load-days strong{margin-top:1px;font-size:8px}.nitrate-load-days i{position:absolute;top:50%;right:7px;width:9px;height:9px;border:1px solid #a9bbc4;border-radius:2px;transform:translateY(-50%)}.nitrate-load p{margin:5px 0 0;color:var(--muted);font-size:6.5px}
            .schedule-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.schedule{min-width:0;border:1px solid var(--line);border-radius:8px;overflow:hidden}.schedule.is-during{grid-column:1 / -1;grid-row:1}.schedule>header{padding:12px 14px;background:#f4f8fa}.schedule.is-during>header{background:var(--pale)}.schedule>header span{display:block;color:var(--blue);font-size:8px;font-weight:800;text-transform:uppercase}.schedule>header strong{display:block;margin-top:2px;font-size:14px}.schedule-body{padding:5px 12px}.schedule-row{display:grid;min-height:48px;padding:7px 0;grid-template-columns:20px 62px minmax(0,1fr) 15px;gap:7px;align-items:center;border-bottom:1px solid #e8eef1}.schedule-row:last-child{border-bottom:0}.schedule-number{display:grid;width:18px;height:18px;place-items:center;border-radius:50%;background:var(--pale);color:#087eb5;font-size:8px;font-weight:800}.schedule-row time{font-size:8px;font-weight:800}.schedule-row strong,.schedule-row small{display:block}.schedule-row strong{font-size:9px;line-height:1.25}.schedule-row small{margin-top:2px;overflow:hidden;color:var(--muted);font-size:7px;text-overflow:ellipsis;white-space:nowrap}.check{display:block;width:13px;height:13px;border:1px solid #a9bbc4;border-radius:3px;background:#fff}.empty{padding:20px 8px;color:var(--muted);font-size:9px;line-height:1.45;text-align:center}
            .schedule-grid.has-day-before{grid-template-columns:repeat(3,minmax(0,1fr))}.schedule-grid.has-day-before .schedule>header strong{font-size:11px}.schedule-grid.has-day-before .schedule-row{grid-template-columns:18px 54px minmax(0,1fr) 13px;gap:5px}
            .checklist{display:grid;margin-top:18px;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.checklist-phase{border:1px solid var(--line);border-radius:8px;overflow:hidden}.checklist-phase h3{margin:0;padding:10px 12px;background:#f4f8fa;font-size:11px}.checklist-phase article{display:grid;min-height:52px;padding:6px 9px;grid-template-columns:14px 34px minmax(0,1fr) auto 38px;gap:7px;align-items:center;border-top:1px solid #e8eef1}.checklist-phase>article>img,.food-icon{display:grid;width:32px;height:32px;place-items:center;border-radius:5px;background:#f4f7f8;object-fit:contain}.food-icon{color:var(--blue);font-weight:800}.checklist-phase strong,.checklist-phase small{display:block}.checklist-phase strong{font-size:9px}.checklist-phase small{margin-top:2px;color:var(--muted);font-size:7px}.checklist-phase article>b{font-size:10px}.product-qr{display:block;color:var(--muted);font-size:6px;font-weight:700;text-align:center;text-decoration:none}.product-qr img{display:block;width:32px;height:32px;margin:0 auto 2px;border-radius:0;background:#fff}.product-qr-spacer{display:block;width:38px}
            .checklist-phase article>b small{display:block;min-width:68px;margin-top:2px;color:var(--muted);font-size:6px;font-weight:600;white-space:nowrap}
            .tips-footer{display:grid;margin-top:12px;padding-top:11px;grid-template-columns:minmax(0,1fr) 108px;gap:18px;border-top:2px solid var(--ink)}.tips h2{margin:0 0 5px;font-size:17px}.training-intro{max-width:720px;margin:0 0 7px;color:var(--muted);font-size:8px;line-height:1.45}.practice-products{display:grid;margin-bottom:7px;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px}.practice-product-group{min-width:0;padding:6px 8px;border:1px solid var(--line);border-radius:6px;background:#fafcfd}.practice-product-group>span{display:block;margin-bottom:3px;color:var(--blue);font-size:6.5px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}.practice-product-group div+div{margin-top:3px;padding-top:3px;border-top:1px solid #e8eef1}.practice-product-group strong,.practice-product-group small{display:block;overflow:visible;text-overflow:clip;white-space:normal;overflow-wrap:anywhere}.practice-product-group strong{font-size:7.5px;line-height:1.25}.practice-product-group small{margin-top:1px;color:var(--muted);font-size:6px;line-height:1.3}.tips .training-checklist{display:grid;margin:0;padding:0;grid-template-columns:1fr 1fr;gap:3px 16px;list-style:none}.tips .training-checklist li{position:relative;padding-left:12px;color:#334f5c;font-size:7.3px;line-height:1.3}.tips .training-checklist li:before{position:absolute;top:3px;left:0;width:6px;height:6px;border-radius:50%;background:var(--blue);content:""}.practice-timelines{margin-top:8px}.practice-timelines-heading{display:flex;margin-bottom:4px;align-items:center;justify-content:space-between;gap:10px}.practice-timelines-heading strong{font-size:10.5px}.practice-timelines-heading span{color:var(--muted);font-size:6.8px}.practice-timeline{display:grid;padding:6px 7px;grid-template-columns:126px minmax(0,1fr);gap:8px;align-items:start;border-top:1px solid var(--line)}.practice-timeline:last-child{border-bottom:1px solid var(--line)}.practice-timeline header{padding-top:2px}.practice-timeline header strong,.practice-timeline header small{display:block}.practice-timeline header strong{font-size:8px;line-height:1.2}.practice-timeline header small{margin-top:2px;color:var(--muted);font-size:6.5px;line-height:1.3}.practice-timeline-track{display:grid;min-width:0;grid-template-columns:repeat(5,minmax(0,1fr));gap:4px;overflow:visible}.practice-timeline-track>p{grid-column:1 / -1;margin:7px 0;color:var(--muted);font-size:7.5px}.practice-step{position:relative;min-width:0;min-height:34px;padding:5px 6px;border:1px solid #d7e9f1;border-radius:5px;background:var(--pale)}.practice-step span,.practice-step strong,.practice-step small{display:block;overflow-wrap:anywhere}.practice-step span{color:#087eb5;font-size:6.2px;font-weight:800;line-height:1.15;text-transform:uppercase}.practice-step strong{margin-top:2px;font-size:7.6px;line-height:1.2}.practice-step small{margin-top:2px;color:var(--muted);font-size:6.4px;line-height:1.15}.practice-step.is-more{border-style:dashed;background:#f4f7f8}.practice-step.is-more span{color:var(--muted)}.qr{text-align:center}.qr a{display:block;color:var(--ink);text-decoration:none}.qr img{display:block;width:82px;height:82px;margin:0 auto 6px}.qr strong,.qr span{display:block}.qr strong{font-size:9px}.qr span{margin-top:3px;color:var(--muted);font-size:7px}.disclaimer{margin:10px 0 0;color:#71838c;font-size:7px;line-height:1.4}.document-footer{display:flex;margin-top:8px;padding-top:7px;align-items:center;justify-content:space-between;border-top:1px solid var(--line);color:var(--muted);font-size:7px}.document-footer a{color:var(--ink);font-weight:700;text-decoration:none}
            .training-supplements{display:grid;margin:0 0 7px;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px}.training-supplements article{padding:6px 8px;border:1px solid #bfe2f2;border-radius:6px;background:#f4fbfe}.training-supplements span,.training-supplements strong{display:block}.training-supplements span{color:var(--blue);font-size:6.5px;font-weight:800;text-transform:uppercase}.training-supplements strong{margin-top:2px;font-size:8px}.training-supplements p{margin:2px 0 0;color:var(--muted);font-size:6.5px;line-height:1.3}
            .sheet:first-child{padding-top:8mm;padding-bottom:8mm}.sheet:first-child .document-header{padding-bottom:14px}.sheet:first-child .hero{padding:17px 0 13px}.sheet:first-child .targets{margin-bottom:13px}.sheet:first-child .target{padding:9px 12px}.sheet:first-child .training-rule{margin-bottom:14px;padding:11px 15px}.sheet:first-child .section-heading{margin-bottom:8px}.sheet:first-child .timeline{margin-bottom:12px;padding:12px 18px 10px}.sheet:first-child .timeline-track{height:76px}.sheet:first-child .schedule-grid{gap:9px}.sheet:first-child .schedule>header{padding:9px 12px}.sheet:first-child .schedule-body{padding:3px 11px}.sheet:first-child .schedule-row{min-height:39px;padding:4px 0}
            @media screen and (max-width:760px){.toolbar span{display:none}.page{width:100%;margin:0}.sheet{padding:24px 18px}.document-header,.hero{grid-template-columns:1fr}.document-type{align-items:flex-start;text-align:left}.hero h1{font-size:31px}.event-meta{min-width:0}.targets,.schedule-grid,.checklist{grid-template-columns:1fr}.target+.target{border-top:1px solid var(--line);border-left:0}.section-heading{align-items:flex-start;flex-direction:column}.section-heading p{text-align:left}.timeline{overflow:hidden}.timeline-track{margin-right:32px;margin-left:32px}.schedule-row{grid-template-columns:20px 65px minmax(0,1fr) 15px}.tips-footer{grid-template-columns:1fr}.tips ul{grid-template-columns:1fr}.qr{text-align:left}.qr img{margin:0 0 6px}}
            @media screen and (max-width:760px){.targets.has-day-before,.schedule-grid.has-day-before{grid-template-columns:1fr}}
            @page{size:A4 portrait;margin:0}
            @media print{html,body{width:210mm!important;min-width:210mm!important;max-width:210mm!important;height:auto!important;margin:0!important;overflow:visible!important;background:#fff}.toolbar{display:none!important}.page{width:210mm!important;max-width:210mm!important;min-height:0!important;height:auto!important;margin:0!important;overflow:visible!important;box-shadow:none}.sheet{width:238.64mm!important;max-width:238.64mm!important;height:336.9mm!important;min-height:336.9mm!important;max-height:336.9mm!important;margin:0!important;padding:9.09mm 11.36mm!important;overflow:hidden!important;zoom:.88;break-after:page!important;page-break-after:always!important;break-inside:avoid!important;page-break-inside:avoid!important}.sheet+.sheet{margin:0!important;padding:9.09mm 11.36mm!important;border-top:0!important}.sheet:last-child{break-after:auto!important;page-break-after:auto!important}.schedule,.checklist-phase,.training-rule,.timeline{break-inside:avoid;page-break-inside:avoid}.document-header{padding-top:0}.qr img{width:82px;height:82px}}
          </style>
        </head>
        <body>
          <nav class="toolbar">
            <div><strong>XMiles printable nutrition plan</strong><span>Review the schedule, then print it or save it as a PDF.</span></div>
            <div><button type="button" onclick="window.close()">Back to plan</button><button class="primary" type="button" onclick="window.print()">Print / Save as PDF</button></div>
          </nav>
          <main class="page">
            <section class="sheet">
              <header class="document-header">
                ${brand}
                <div class="document-type"><span>Event nutrition plan</span><strong>Prepared with XMiles</strong></div>
              </header>
              <section class="hero">
                <div><p class="eyebrow">${escapeHtml(eventLine)}</p><h1>${escapeHtml(plan?.title || "Your nutrition plan")}</h1><p>A practical timing guide for training and event day.</p></div>
                <div class="event-meta${eventDateLabel ? "" : " is-single"}">${eventDateLabel ? `<div><span>Event date</span><strong>${escapeHtml(eventDateLabel)}</strong></div>` : ""}<div><span>Predicted time</span><strong>${escapeHtml(durationLabel(plan?.durationMinutes))}</strong></div></div>
              </section>
              <section class="targets">${phaseTargetMarkup(plan, selections)}</section>
              <div class="section-heading"><div><span>Your event timeline</span><h2>Fuel across ${escapeHtml(durationLabel(plan?.durationMinutes))}</h2></div><p>${escapeHtml(timelineGuidance)}</p></div>
              <section class="timeline">${racePhaseTimelineMarkup(plan)}${supplementTimelineMarkup(bicarbProducts, nitrateProducts)}${duringTimelineMarkup(duringSchedule, plan?.durationMinutes)}</section>
              ${nitrateLoadMarkup(nitrateProducts)}
              <section class="schedule-grid">
                <div class="schedule"><header><span>${beforeLabel}</span><strong>Arrive fuelled</strong></header><div class="schedule-body">${scheduleRowsMarkup(beforeSchedule, "No pre-session products selected.")}</div></div>
                <div class="schedule is-during"><header><span>During</span><strong>Follow elapsed time</strong></header><div class="schedule-body">${scheduleRowsMarkup(duringSchedule, "No during-session products selected.")}</div></div>
                <div class="schedule"><header><span>After</span><strong>Start recovery</strong></header><div class="schedule-body">${scheduleRowsMarkup(afterSchedule, "No recovery products selected.")}</div></div>
              </section>
            </section>
            <section class="sheet">
              <div class="section-heading"><div><span>Preparation</span><h2>Pack checklist</h2></div><p>Tick each item as it is packed. Multipacks show exactly how many servings to use.</p></div>
              <section class="checklist">${productChecklistMarkup(selections, plan) || '<p class="empty">No products have been added to this plan yet.</p>'}</section>
              <section class="tips-footer">
                <div class="tips"><h2>Make the plan work in training</h2>${runningTrainingPlan || `<ul>${trainingTips.map((tip) => `<li>${escapeHtml(tip)}</li>`).join("")}<li>Use your tested hydration plan and adjust fluid and sodium for weather, sweat rate and aid-station access.</li><li>Review the live plan after each rehearsal and update anything that was difficult to carry, open, eat or drink.</li></ul>`}</div>
                <div class="qr"><a href="${escapeHtml(safePlanUrl)}"><img src="${escapeHtml(qrUrl)}" alt="QR code linking to this nutrition plan"><strong>Open live plan</strong><span>Scan to review or update</span></a></div>
              </section>
              <p class="disclaimer">This plan is general sports-nutrition guidance, not medical advice. Individual needs and product tolerances vary. Follow product instructions and seek advice from a qualified sports dietitian or healthcare professional if you have a medical condition, allergy or specific dietary concern.</p>
              <footer class="document-footer"><span>Generated ${escapeHtml(new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(new Date()))}</span><a href="https://xmiles.co.uk">xmiles.co.uk</a></footer>
            </section>
          </main>
        </body>
      </html>`;
  };

  const open = (options = {}) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return false;
    printWindow.document.open();
    printWindow.document.write(printableMarkup(options));
    printWindow.document.close();
    printWindow.opener = null;
    printWindow.focus();
    return true;
  };

  window.XMilesNutritionPlanPrint = { open };
})();
